import React, { useEffect, useState } from 'react';

interface CustomSpacingModalProps {
  /** Passed in from the editor, which is where the language hook lives */
  t: any;
  show: boolean;
  onClose: () => void;
  initialLineSpacing: number;
  initialSpacingBefore: number;
  initialSpacingAfter: number;
  onApply: (lineSpacing: number, spacingBefore: number, spacingAfter: number) => void;
}

export const CustomSpacingModal: React.FC<CustomSpacingModalProps> = ({
  t,
  show,
  onClose,
  initialLineSpacing,
  initialSpacingBefore,
  initialSpacingAfter,
  onApply,
}) => {
  const [customLineSpacing, setCustomLineSpacing] = useState(initialLineSpacing.toString());
  const [customSpacingBefore, setCustomSpacingBefore] = useState(initialSpacingBefore.toString());
  const [customSpacingAfter, setCustomSpacingAfter] = useState(initialSpacingAfter.toString());

  // The dialog stays mounted, so reload the selection's values each time it
  // opens instead of showing whatever was typed last session.
  useEffect(() => {
    if (!show) return;
    setCustomLineSpacing(initialLineSpacing.toString());
    setCustomSpacingBefore(initialSpacingBefore.toString());
    setCustomSpacingAfter(initialSpacingAfter.toString());
  }, [show, initialLineSpacing, initialSpacingBefore, initialSpacingAfter]);

  if (!show) return null;

  const handleApply = () => {
    const lineSpacingValue = parseFloat(customLineSpacing) || 1.0;
    const spacingBeforeValue = parseInt(customSpacingBefore) || 0;
    const spacingAfterValue = parseInt(customSpacingAfter) || 0;

    onApply(lineSpacingValue, spacingBeforeValue, spacingAfterValue);
    onClose();
  };

  return (
    <div className="custom-spacing-modal-overlay" onClick={onClose}>
      <div className="custom-spacing-modal" onClick={(e) => e.stopPropagation()}>
        <div className="custom-spacing-modal-header">
          <h3>{t.editor.customSpacing}</h3>
          <button 
            className="custom-spacing-close-btn"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        <div className="custom-spacing-content">
          <div className="custom-spacing-field">
            <label htmlFor="line-spacing-input">{t.editor.lineSpacing}</label>
            <input
              id="line-spacing-input"
              type="text"
              className="custom-spacing-input"
              value={customLineSpacing}
              onChange={(e) => setCustomLineSpacing(e.target.value)}
              placeholder="1.0"
            />
          </div>
          
          <h4 className="custom-spacing-subheading">{t.editor.paragraphSpacingPts}</h4>
          
          <div className="custom-spacing-field">
            <label htmlFor="spacing-before-input">{t.editor.spacingBefore}</label>
            <input
              id="spacing-before-input"
              type="text"
              className="custom-spacing-input"
              value={customSpacingBefore}
              onChange={(e) => setCustomSpacingBefore(e.target.value)}
              placeholder="0"
            />
          </div>
          
          <div className="custom-spacing-field">
            <label htmlFor="spacing-after-input">{t.editor.spacingAfter}</label>
            <input
              id="spacing-after-input"
              type="text"
              className="custom-spacing-input"
              value={customSpacingAfter}
              onChange={(e) => setCustomSpacingAfter(e.target.value)}
              placeholder="0"
            />
          </div>
        </div>
        <div className="custom-spacing-buttons">
          <button 
            className="custom-spacing-btn cancel"
            onClick={onClose}
          >
            {t.common.cancel}
          </button>
          <button 
            className="custom-spacing-btn apply"
            onClick={handleApply}
          >
            {t.editor.apply}
          </button>
        </div>
      </div>
    </div>
  );
};
